import * as THREE from 'three';
import { aimMove, scaleFromLift, slide, type Point } from '../engine/placement.js';
import type { LockButton } from './lock-button.js';

/**
 * Handles for moving and resizing a thing in an AR room, from where the
 * student stands.
 *
 * Carrying the skeleton by hand meant pinching inside it, so to bring it
 * closer a student had to walk up to it first -- and it arrives 1.6 m away,
 * out of reach. Pieter: "I don't get the skeleton to move any closer to me."
 * A handle is dragged by a pointer or a pinch from wherever the student is:
 *
 * - **Move** follows where the pointer meets the handle's own height, so the
 *   thing slides across the room: aiming steeper brings it closer.
 * - **Resize** follows the pointer up and down: lift it to grow, lower it to
 *   shrink, measured from the thing's base.
 *
 * The arithmetic is `engine/placement.ts` and is tested there.
 *
 * Handles appear only while the thing is unlocked (LockButton): what is
 * locked has nothing to grab.
 */

const DIAMETER = 0.09;
/** Space between buttons in a toolbar, in the world, whatever the scale. */
const SPACING = 0.11;
/** How close a pinch must be to a handle to take hold of it. */
const HAND_REACH = 0.04;

const MOVABLE = '#f59e0b';

export type HandleKind = 'move' | 'resize';

/** A round button with an icon: amber, because it means "this moves". */
export class HandleButton {
  readonly mesh: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>;
  readonly #texture: THREE.CanvasTexture;

  constructor(
    parent: THREE.Object3D,
    readonly kind: HandleKind,
  ) {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    draw(canvas.getContext('2d')!, kind);
    this.#texture = new THREE.CanvasTexture(canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;

    this.mesh = new THREE.Mesh(
      new THREE.CircleGeometry(DIAMETER / 2, 32),
      new THREE.MeshBasicMaterial({ map: this.#texture, transparent: true }),
    );
    this.mesh.renderOrder = 5;
    this.mesh.userData['handle'] = this;
    parent.add(this.mesh);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.#texture.dispose();
  }
}

function arrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): void {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - 13 * Math.cos(angle - 0.55), y2 - 13 * Math.sin(angle - 0.55));
  ctx.lineTo(x2 - 13 * Math.cos(angle + 0.55), y2 - 13 * Math.sin(angle + 0.55));
  ctx.closePath();
  ctx.fill();
}

function draw(ctx: CanvasRenderingContext2D, kind: HandleKind): void {
  const size = 128;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 3, 0, Math.PI * 2);
  ctx.fillStyle = MOVABLE;
  ctx.fill();
  ctx.lineWidth = 5;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.stroke();

  ctx.strokeStyle = '#ffffff';
  ctx.fillStyle = '#ffffff';
  ctx.lineWidth = 8;
  ctx.lineCap = 'round';
  if (kind === 'move') {
    // Four ways: this slides across the room.
    arrow(ctx, 64, 64, 64, 22);
    arrow(ctx, 64, 64, 64, 106);
    arrow(ctx, 64, 64, 22, 64);
    arrow(ctx, 64, 64, 106, 64);
  } else {
    // Corner to corner: bigger or smaller.
    arrow(ctx, 64, 64, 30, 30);
    arrow(ctx, 64, 64, 98, 98);
  }
}

/**
 * A padlock with its handles stacked under it, beside the thing they move.
 *
 * Laid out every frame in the world, not in the thing's own space: a skeleton
 * shrunk onto a desk would otherwise squeeze its buttons on top of each other
 * while each stays the same size to press.
 */
export class Toolbar {
  constructor(
    private readonly target: THREE.Object3D,
    /** Where the padlock goes, in the target's space. */
    private readonly anchor: THREE.Vector3,
    readonly lock: LockButton,
    readonly handles: readonly HandleButton[],
    /**
     * Which way the handles line up from the padlock, in the target's space.
     * Sideways by default: stacked downwards they ran into the instruction
     * panel in front of the skeleton's chest, which hid the resize handle.
     */
    private readonly along = new THREE.Vector3(-1, 0, 0),
  ) {}

  update(camera: THREE.Camera): void {
    const scale = this.target.getWorldScale(new THREE.Vector3()).x || 1;
    this.lock.mesh.position.copy(this.anchor);
    this.lock.update(camera);

    const unlocked = !this.lock.locked;
    for (const [i, handle] of this.handles.entries()) {
      handle.mesh.visible = unlocked && this.lock.mesh.visible;
      handle.mesh.position
        .copy(this.anchor)
        .addScaledVector(this.along, ((i + 1) * SPACING) / scale);
      handle.mesh.lookAt(camera.getWorldPosition(new THREE.Vector3()));
      handle.mesh.scale.setScalar(1 / scale);
    }
  }

  dispose(): void {
    for (const handle of this.handles) handle.dispose();
  }
}

/** Something a drag can move, and whether it may be resized. */
export type Draggable = {
  object: THREE.Object3D;
  resizable: boolean;
  /**
   * Things inside the object that keep their place in the room while it is
   * moved or resized: the tray of loose bones, which lives in the skeleton's
   * space so its bones are the skeleton's size, but has a handle of its own.
   */
  stays?: readonly THREE.Object3D[];
};

type Grip = { hand: number } | 'pointer';

type Drag =
  | {
      kind: 'move';
      target: Draggable;
      by: Grip;
      startObject: Point;
      /** Where the hand took hold; a hand slides the object along with it. */
      startGrip: Point;
      /** Where the pointer was aimed from, and which way; a pointer aims it. */
      eye: Point;
      startDirection: Point;
    }
  | {
      kind: 'resize';
      target: Draggable;
      by: Grip;
      baseY: number;
      startY: number;
      startScale: number;
      plane: THREE.Plane;
    };

const toPoint = (v: THREE.Vector3): Point => [v.x, v.y, v.z];

/**
 * One drag at a time, by a pointer or by a pinch.
 *
 * Pointers are a controller, the laptop mouse, or a hand's own ray -- a
 * student who cannot reach the skeleton points at its handle instead. Pinches
 * take hold of a handle within a few centimetres of the fingertips, which is
 * how a trolley's rail is grabbed.
 */
export class Dragger {
  #drag: Drag | undefined;

  get active(): boolean {
    return this.#drag !== undefined;
  }

  /** The hand holding something, if a hand is. */
  get hand(): number | undefined {
    const by = this.#drag?.by;
    return by !== undefined && by !== 'pointer' ? by.hand : undefined;
  }

  /** A pointer pressed on a handle. `at` is where it hit. */
  beginPointer(kind: HandleKind, target: Draggable, ray: THREE.Ray, at: THREE.Vector3): void {
    this.#begin(kind, target, 'pointer', at, ray.origin, ray.direction);
  }

  /** A pinch took hold of a handle at `at`. */
  beginHand(
    kind: HandleKind,
    target: Draggable,
    hand: number,
    at: THREE.Vector3,
    eye: THREE.Vector3,
  ): void {
    this.#begin(kind, target, { hand }, at, eye, at.clone().sub(eye).normalize());
  }

  movePointer(ray: THREE.Ray): void {
    const drag = this.#drag;
    if (!drag || drag.by !== 'pointer') return;

    if (drag.kind === 'move') {
      this.#place(
        drag,
        aimMove({
          eye: drag.eye,
          object: drag.startObject,
          from: drag.startDirection,
          to: toPoint(ray.direction),
        }),
      );
    } else {
      const grip = ray.intersectPlane(drag.plane, new THREE.Vector3());
      if (grip) this.#resize(drag, grip.y);
    }
  }

  moveHand(hand: number, at: THREE.Vector3): void {
    const drag = this.#drag;
    if (!drag || drag.by === 'pointer' || drag.by.hand !== hand) return;
    if (drag.kind === 'move')
      this.#place(drag, slide(drag.startObject, drag.startGrip, toPoint(at)));
    else this.#resize(drag, at.y);
  }

  end(): void {
    this.#drag = undefined;
  }

  #begin(
    kind: HandleKind,
    target: Draggable,
    by: Grip,
    at: THREE.Vector3,
    eye: THREE.Vector3,
    direction: THREE.Vector3,
  ): void {
    const object = target.object;
    if (kind === 'move') {
      this.#drag = {
        kind,
        target,
        by,
        startObject: toPoint(object.getWorldPosition(new THREE.Vector3())),
        startGrip: toPoint(at),
        eye: toPoint(eye),
        startDirection: toPoint(direction),
      };
      return;
    }
    if (!target.resizable) return;

    // A vertical plane through the handle, facing the student, for a pointer
    // to be followed up and down on.
    const facing = eye.clone().sub(at).setY(0);
    if (facing.lengthSq() < 1e-6) facing.set(0, 0, 1);
    this.#drag = {
      kind,
      target,
      by,
      baseY: object.getWorldPosition(new THREE.Vector3()).y,
      startY: at.y,
      startScale: object.scale.x,
      plane: new THREE.Plane().setFromNormalAndCoplanarPoint(facing.normalize(), at),
    };
  }

  #place(drag: Extract<Drag, { kind: 'move' }>, world: Point): void {
    keepingInPlace(drag.target, (object) => {
      const local = object.parent
        ? object.parent.worldToLocal(new THREE.Vector3(...world))
        : new THREE.Vector3(...world);
      // Only across: it keeps the height it had, in its own space.
      object.position.x = local.x;
      object.position.z = local.z;
    });
  }

  #resize(drag: Extract<Drag, { kind: 'resize' }>, nowY: number): void {
    keepingInPlace(drag.target, (object) =>
      object.scale.setScalar(
        scaleFromLift({
          startScale: drag.startScale,
          baseY: drag.baseY,
          startY: drag.startY,
          nowY,
        }),
      ),
    );
  }
}

/**
 * Stands `target.object` at `height` in the world -- on the table top or the
 * floor found beneath it -- leaving whatever `stays` where it is.
 */
export function settle(target: Draggable, height: number): void {
  keepingInPlace(target, (object) => {
    const at = object.getWorldPosition(new THREE.Vector3());
    at.y = height;
    object.position.y = object.parent ? object.parent.worldToLocal(at).y : height;
  });
}

/** Changes `target.object`, putting whatever `stays` back where it was. */
function keepingInPlace(target: Draggable, change: (object: THREE.Object3D) => void): void {
  const stays = target.stays ?? [];
  const were = stays.map((thing) => thing.getWorldPosition(new THREE.Vector3()));
  change(target.object);
  target.object.updateMatrixWorld(true);
  for (const [i, thing] of stays.entries()) {
    const at = were[i];
    if (!at || !thing.parent) continue;
    thing.position.copy(thing.parent.worldToLocal(at));
  }
}

/**
 * How close a pointer must pass to a rail to take hold of it. The tube is two
 * centimetres thick; hit exactly, a mouse missed it from a metre away and a
 * controller ray, which wobbles more, would miss it more.
 */
const RAIL_AIM = 0.05;

/**
 * Where a pointer takes hold of a rail running along `path`: the nearest point
 * on the rail it passes within reach of, or nothing if it passes clear.
 */
export function railAlong(
  ray: THREE.Ray,
  path: readonly THREE.Vector3[],
): THREE.Vector3 | undefined {
  let best: { at: THREE.Vector3; distanceSq: number } | undefined;
  for (let i = 0; i + 1 < path.length; i++) {
    const from = path[i];
    const to = path[i + 1];
    if (!from || !to) continue;
    const at = new THREE.Vector3();
    const distanceSq = ray.distanceSqToSegment(from, to, undefined, at);
    if (distanceSq <= RAIL_AIM * RAIL_AIM && (!best || distanceSq < best.distanceSq)) {
      best = { at, distanceSq };
    }
  }
  return best?.at;
}

/**
 * The handle a pinch at `point` is taking hold of, if any: a round button
 * within reach, or a rail whose path passes within reach.
 */
export function handleNear(
  point: THREE.Vector3,
  buttons: readonly HandleButton[],
  rails: readonly { path: () => THREE.Vector3[]; shown: () => boolean }[],
): { kind: 'button'; button: HandleButton } | { kind: 'rail'; index: number } | undefined {
  for (const button of buttons) {
    if (!visibleInWorld(button.mesh)) continue;
    const at = button.mesh.getWorldPosition(new THREE.Vector3());
    if (at.distanceTo(point) <= DIAMETER / 2 + HAND_REACH) return { kind: 'button', button };
  }
  for (const [index, rail] of rails.entries()) {
    if (!rail.shown()) continue;
    const path = rail.path();
    for (let i = 0; i + 1 < path.length; i++) {
      const closest = new THREE.Line3(path[i], path[i + 1]).closestPointToPoint(
        point,
        true,
        new THREE.Vector3(),
      );
      if (closest.distanceTo(point) <= HAND_REACH) return { kind: 'rail', index };
    }
  }
  return undefined;
}

function visibleInWorld(object: THREE.Object3D): boolean {
  for (let at: THREE.Object3D | null = object; at; at = at.parent) if (!at.visible) return false;
  return true;
}
